import * as Sentry from '@sentry/react';
import { Alert } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { t } from 'ttag';

import Loading from 'common/components/Loading';
import useSearchParams from 'common/hooks/useSearchParams';

import { parseBadgeData } from '@domains/Badges/designer-service';
import { useBadgePeopleQuery, useBadgeQuery, usePersonQuery } from '@domains/Badges/queries';

import Builder from '../../Builder';
import {
  COLUMN_SIZE,
  UNIT_BLEED_LENGTH,
  UNIT_CROP_MARK_LENGTH,
  UNIT_DIVISION,
} from '../../Builder/constants';
import { BadgeTypes, UNIT_INCH, UNIT_MM } from '../../constants';
import Grid, { getGridProps } from '../../Download/BadgeGrid';
import { Page, CropMarksContainer, CropMarks } from '../../Download/PagePreview';

import { MOCK_PERSON_DATA_FOR_TESTING } from './constants';

const NOT_FOUND_ERROR_CODES = ['badges_not_found'];

const DEFAULT_SIMPLE_LABEL_CONFIG = {
  layout_unit: UNIT_MM,
  layout_spacing_column: '0',
  layout_spacing_row: '0',
  layout_margins_top: '0',
  layout_margins_right: '0',
  layout_margins_bottom: '0',
  layout_margins_left: '0',
  sort: 'first_name',
  fill: 'across',
  border: 'false',
  crop_marks: 'false',
};

const PageStyle = createGlobalStyle`
  /* Hide elements that cause blank pages to appear */
  .ReactQueryDevtools, .jodit-box {
    display: none;
  }

  // @page tells playwright what size the actual page is
  // it relies on 'prefer_css_page_size' param to be true in
  // https://playwright.dev/python/docs/api/class-page#page-pdf-option-prefer-css-page-size
  //
  // it also tells page size to the print dialog in browser
  // when you press cmd+p / ctrl+p
  @page {
    size: ${props => props.width * props.scale}px ${props => props.height * props.scale}px;
  }

  ${Page} {
    // in some cases we need to rescale the page as correction measure
    //
    // we used 'zoom', instead of 'transform: scale()'
    // 'transform: scale()' only scales the content inside it, while
    // occupying the original size of block on page
    // while 'zoom' scales both.
    //
    // IMPORTANT: 'zoom' is a non-standard css feature
    // https://developer.mozilla.org/en-US/docs/Web/CSS/zoom
    zoom: ${props => props.scale};
  }
`;

const roundFloat = v => parseFloat(v.toFixed(1));
const normalizeUnit = (v, unit) =>
  (unit === UNIT_INCH ? roundFloat(v * UNIT_DIVISION[UNIT_INCH]) : v) * COLUMN_SIZE;

const getNextBadges = ({ page, gridProps, badges }) => {
  const { rows, cols } = gridProps;
  const offset = page * rows * cols;
  const sortedBadges = [...badges].slice(offset, offset + rows * cols);
  return sortedBadges;
};

const parseConfig = (query, badge) => {
  const paramKeys = [
    'page_size_width',
    'page_size_height',
    'page_size_unit',
    'layout_unit',
    'layout_margins_top',
    'layout_margins_right',
    'layout_margins_bottom',
    'layout_margins_left',
    'layout_spacing_column',
    'layout_spacing_row',
    'sort',
    'fill',
    'border',
    'crop_marks',
  ];

  let initialConfig = Object.fromEntries(query.entries());

  if (badge.badgeType === BadgeTypes.SIMPLE_LABEL) {
    initialConfig = { ...initialConfig, ...DEFAULT_SIMPLE_LABEL_CONFIG };
  }

  const missingParamKeys = paramKeys.filter(key => !initialConfig[key]);
  if (missingParamKeys.length > 0) {
    const list = missingParamKeys.map(key => `'${key}'`);
    throw new Error(`${list.join(', ')} query param is/are required.`);
  }

  const rawConfig = {
    pageSize: {
      width: parseFloat(initialConfig.page_size_width),
      height: parseFloat(initialConfig.page_size_height),
      unit: initialConfig.page_size_unit,
    },
    layout: {
      unit: initialConfig.layout_unit,
      margins: {
        top: parseFloat(initialConfig.layout_margins_top),
        right: parseFloat(initialConfig.layout_margins_right),
        bottom: parseFloat(initialConfig.layout_margins_bottom),
        left: parseFloat(initialConfig.layout_margins_left),
      },
      spacing: {
        column: parseFloat(initialConfig.layout_spacing_column),
        row: parseFloat(initialConfig.layout_spacing_row),
      },
    },
    sort: initialConfig.sort,
    fill: initialConfig.fill,
    border: initialConfig.border.toLowerCase() === 'true',
    cropMarks: initialConfig.crop_marks.toLowerCase() === 'true',
    scale: parseFloat(initialConfig.scale ?? 1),
  };

  const pageSizeUnit = rawConfig.pageSize.unit;
  const layoutUnit = rawConfig.layout.unit;
  const badgeSizeUnit = badge.size.unit;

  const config = {
    renderOptions: {
      gridProps: {
        /* to be filled later */
      },
    },
    pageSize: {
      width: normalizeUnit(rawConfig.pageSize.width, pageSizeUnit),
      height: normalizeUnit(rawConfig.pageSize.height, pageSizeUnit),
      unit: pageSizeUnit,
    },
    margins: {
      top: normalizeUnit(rawConfig.layout.margins.top, layoutUnit),
      right: normalizeUnit(rawConfig.layout.margins.right, layoutUnit),
      bottom: normalizeUnit(rawConfig.layout.margins.bottom, layoutUnit),
      left: normalizeUnit(rawConfig.layout.margins.left, layoutUnit),
    },
    spacing: {
      column: normalizeUnit(rawConfig.layout.spacing.column, layoutUnit),
      row: normalizeUnit(rawConfig.layout.spacing.row, layoutUnit),
    },
    badgeSize: {
      width: normalizeUnit(parseFloat(badge.size.width), badgeSizeUnit),
      height: normalizeUnit(parseFloat(badge.size.height), badgeSizeUnit),
    },
    sort: rawConfig.sort,
    fill: rawConfig.fill,
    border: rawConfig.border,
    cropMarks: rawConfig.cropMarks,
    scale: rawConfig.scale,
  };

  config.renderOptions.gridProps = getGridProps(
    config.pageSize.width - config.margins.left - config.margins.right,
    config.pageSize.height - config.margins.top - config.margins.bottom,
    config.spacing,
    config.badgeSize,
  );

  return config;
};

const Pages = ({ config, badge, design, people }) => {
  const {
    renderOptions: { gridProps },
    pageSize,
    margins,
    cropMarks,
  } = config;

  const badges = people.map(person => {
    const item = (
      <Builder
        key={person.id}
        badge={{
          badgeType: badge.badgeType,
          size: badge.size,
          orientation: badge.orientation,
          background: design.background,
          widgets: design.widgets,
          isBleedEnabled: badge.isBleedEnabled,
        }}
        isLoading={false}
        person={person}
        showBorder={config.border}
      />
    );

    if (cropMarks) {
      const bleedGap = UNIT_BLEED_LENGTH[pageSize.unit];
      const cropMarkLength = UNIT_CROP_MARK_LENGTH[pageSize.unit];
      return (
        <CropMarksContainer key={`${person.id}-crop-marks`}>
          <CropMarks bleedGap={bleedGap} cropMarkLength={cropMarkLength} unit={pageSize.unit} />
          {item}
        </CropMarksContainer>
      );
    }
    return item;
  });

  const badgeCount = badges.length;
  const totalPages = Math.ceil(badgeCount / (gridProps.rows * gridProps.cols));

  const pages = Array(totalPages)
    .fill(0)
    .map((_, index) => (
      <Page
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        width={pageSize.width}
        height={pageSize.height}
        margin={margins}
        cropMarks={cropMarks}
      >
        <Grid {...gridProps} fill={config.fill}>
          {getNextBadges({ page: index, gridProps, badges })}
        </Grid>
      </Page>
    ));

  return pages;
};

const MOCK_PEOPLE = [MOCK_PERSON_DATA_FOR_TESTING];

/*
  There are three different cases here - one person, all applied people, mock data.

  We can't render hooks conditionally unless all of the conditional values are
  static at runtime (i.e. a feature flag or the URL params here), and these
  queries are dependent on badgeData coming from another query, so we're stuck
  with:
  - Setting up all three cases
  - Disabling the queries we don't need
  - Returning the result of the case we want
*/
const useCapturePeopleQuery = (eventId, badgeId, badge, query, sort) => {
  const token = query.get('token');
  const limit = Number(query.get('limit') ?? 1000);
  const offset = Number(query.get('page') ?? 0) * limit;
  const personId = query.get('people_id');
  const usingMockPeopleData = query.get('use_mock_people_data')?.toLowerCase() === 'true';

  const personResult = usePersonQuery(eventId, personId, token, {
    enabled: badge?.badgeType === BadgeTypes.SIMPLE_LABEL && personId && !usingMockPeopleData,
    select: useCallback(person => [person], []),
  });

  const peopleResult = useBadgePeopleQuery(
    {
      id: badgeId,
      appliedTo: badge?.appliedTo,
    },
    {
      token,
      limit,
      offset,
      sort,
    },
    {
      enabled: !!badge && badge.badgeType !== BadgeTypes.SIMPLE_LABEL && !usingMockPeopleData,
      select: res => res.data,
    },
  );

  const mockPeopleResult = useMemo(
    () => ({ data: MOCK_PEOPLE, isLoading: false, error: null }),
    [],
  );

  return (usingMockPeopleData && mockPeopleResult) || (personId && personResult) || peopleResult;
};

const CapturePDF = () => {
  const query = useSearchParams();
  const { eventId, resourceId: badgeId } = useParams();
  const token = query.get('token');

  const {
    data: badgeData,
    isLoading: isBadgeLoading,
    error: badgeError,
  } = useBadgeQuery(badgeId, token, {
    select: useCallback(res => parseBadgeData(res), []),
  });
  const { badge, design } = badgeData ?? {};
  const config = badge ? parseConfig(query, badge) : null;
  const {
    data: people,
    isLoading: isPeopleLoading,
    error: peopleError,
  } = useCapturePeopleQuery(eventId, badgeId, badge, query, config?.sort);

  const isLoading = isBadgeLoading || isPeopleLoading;
  const error = badgeError || peopleError;

  const notFound = NOT_FOUND_ERROR_CODES.includes(error?.errors?.[0]?.code);
  const errorMessage = notFound ? t`Resource was not found.` : error?.message;

  useEffect(() => {
    if (error && !notFound) {
      Sentry.captureException(error);
    }
  }, [error, notFound]);

  if (error) {
    return <Alert type="error" message={errorMessage} showIcon />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <PageStyle
        width={config.pageSize.width}
        height={config.pageSize.height}
        scale={config.scale}
      />
      <Pages config={config} badge={badge} design={design} people={people} />
    </>
  );
};

export default CapturePDF;
